import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { pb } from "@/lib/pocketbase";
import { toast } from "sonner";
import { SanityCredentials, SanityPublishOptions } from "@/types/integration";
import { SanityValidationResult } from "@/services/sanity";
import { CheckCircle2 } from "lucide-react";

interface SanityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  brandId: string;
}

interface SanitySettings extends SanityCredentials {
  documentTypes?: string[];
  selectedDocumentType?: string;
  defaultPublishOptions?: SanityPublishOptions;
  autoPublish?: boolean;
  defaultAuthor?: string;
}

export function SanityDialog({
  open,
  onOpenChange,
  onSuccess,
  brandId,
}: SanityDialogProps) {
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTested, setConnectionTested] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [typeValidation, setTypeValidation] = useState<
    Record<string, SanityValidationResult>
  >({});
  const [fetchingDocTypes, setFetchingDocTypes] = useState(false);
  const [sanitySettings, setSanitySettings] = useState<SanitySettings>({
    projectId: "",
    dataset: "production",
    token: "",
    studioUrl: "",
    selectedDocumentType: "post",
    autoPublish: true,
    defaultAuthor: "",
    defaultPublishOptions: {
      returnIds: true,
      returnDocuments: false,
      visibility: "sync",
      dryRun: false,
    },
  });

  const testConnection = async () => {
    if (!brandId) {
      toast.error("Please select a brand first");
      return;
    }

    if (
      !sanitySettings.projectId ||
      !sanitySettings.dataset ||
      !sanitySettings.token
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setTestingConnection(true);
    try {
      const response = await fetch(
        `${pb.baseURL}/api/v1/sanity/test-connection`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
          body: JSON.stringify({
            brandId,
            projectId: sanitySettings.projectId,
            dataset: sanitySettings.dataset,
            token: sanitySettings.token,
            studioUrl: sanitySettings.studioUrl,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to connect to Sanity");
      }

      setConnectionTested(true);
      toast.success("Connection successful!");

      // Fetch document types after successful connection
      await fetchDocumentTypes();
    } catch (error) {
      console.error("Error testing connection:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to connect to Sanity",
      );
      setConnectionTested(false);
    } finally {
      setTestingConnection(false);
    }
  };

  const fetchDocumentTypes = async () => {
    if (
      !sanitySettings.projectId ||
      !sanitySettings.dataset ||
      !sanitySettings.token
    ) {
      return;
    }

    setFetchingDocTypes(true);
    try {
      const response = await fetch(
        `${pb.baseURL}/api/v1/sanity/document-types`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
          body: JSON.stringify({
            brandId,
            projectId: sanitySettings.projectId,
            dataset: sanitySettings.dataset,
            token: sanitySettings.token,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch document types");
      }

      const data = await response.json();
      setDocumentTypes(data.document_types || []);
    } catch (error) {
      console.error("Error fetching document types:", error);
      toast.error("Failed to fetch document types from Sanity");
    } finally {
      setFetchingDocTypes(false);
    }
  };

  const handleSanityConnect = async () => {
    if (!connectionTested) {
      toast.error("Please test the connection first");
      return;
    }

    setLoading(true);
    try {
      // Save the integration with the current settings
      // The connection was already tested and saved by the test-connection endpoint
      onOpenChange(false);
      onSuccess();
      toast.success("Successfully connected to Sanity");

      // Reset form
      setSanitySettings({
        projectId: "",
        dataset: "production",
        token: "",
        studioUrl: "",
        selectedDocumentType: "post",
        autoPublish: true,
        defaultAuthor: "",
        defaultPublishOptions: {
          returnIds: true,
          returnDocuments: false,
          visibility: "sync",
          dryRun: false,
        },
      });
      setConnectionTested(false);
      setDocumentTypes([]);
    } catch (error) {
      console.error("Error connecting to Sanity:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to connect to Sanity",
      );
    } finally {
      setLoading(false);
    }
  };

  // Get validation status for a document type
  const getValidationStatus = (docType: string) => {
    const validation = typeValidation[docType];
    if (!validation) return null;

    if (!validation.valid) return "error";
    if (validation.warnings && validation.warnings.length > 0) return "warning";
    return "success";
  };

  // Get validation icon for a document type
  const getValidationIcon = (docType: string) => {
    const status = getValidationStatus(docType);
    switch (status) {
      case "error":
        return "❌";
      case "warning":
        return "⚠️";
      case "success":
        return "✅";
      default:
        return "❓";
    }
  };

  // Get validation message for a document type
  const getValidationMessage = (docType: string) => {
    const validation = typeValidation[docType];
    if (!validation) return "Validation status unknown";

    const messages = [];
    if (validation.errors && validation.errors.length > 0) {
      messages.push(`Errors: ${validation.errors.join(", ")}`);
    }
    if (validation.warnings && validation.warnings.length > 0) {
      messages.push(`Warnings: ${validation.warnings.join(", ")}`);
    }
    if (validation.skipped && validation.skipped.length > 0) {
      messages.push(`Skipped fields: ${validation.skipped.join(", ")}`);
    }

    return messages.length > 0
      ? messages.join(" | ")
      : "Schema is fully compatible";
  };

  // Reset connection tested status when credentials change
  useEffect(() => {
    setConnectionTested(false);
    setDocumentTypes([]);
    setTypeValidation({});
  }, [sanitySettings.projectId, sanitySettings.dataset, sanitySettings.token]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect Sanity CMS</DialogTitle>
          <DialogDescription>
            Enter your Sanity project credentials to connect your CMS
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-6">
          {/* Instructions */}
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-slate-50">
              <h4 className="font-medium mb-2">
                How to get Sanity credentials:
              </h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Log in to your Sanity account at sanity.io</li>
                <li>Go to your project dashboard</li>
                <li>Copy your Project ID from the project settings</li>
                <li>Choose your dataset (usually "production")</li>
                <li>Go to Settings → API → Tokens</li>
                <li>Click "Add API token"</li>
                <li>Set permissions to "Editor" and create token</li>
                <li>Copy the generated token</li>
              </ol>
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Required permissions:</strong> Editor access to create
                  and update documents
                </p>
              </div>
            </div>

            <div className="rounded-lg border p-4 bg-amber-50">
              <h4 className="font-medium mb-2 text-amber-800">
                Schema Requirements
              </h4>
              <p className="text-sm text-amber-700 mb-2">
                Make sure your Sanity schema includes a document type with
                fields: title, slug, body, mainImage, and seo for proper
                integration.
              </p>
              <p className="text-xs text-amber-600">
                ✓ Supports dynamic schema detection
                <br />
                ✓ Auto-detects image field names
                <br />
                ✓ Batch publishing support
                <br />✓ Transaction-safe operations
              </p>
            </div>

            {connectionTested && (
              <div className="rounded-lg border p-4 bg-green-50">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-medium text-green-800">
                    Connection Verified
                  </h4>
                </div>
                {documentTypes.length > 0 && (
                  <div>
                    <p className="text-sm text-green-700 mb-2">
                      Available document types:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {documentTypes.map((type) => {
                        const status = getValidationStatus(type);
                        return (
                          <Badge
                            key={type}
                            variant={
                              status === "error"
                                ? "destructive"
                                : status === "warning"
                                  ? "outline"
                                  : "secondary"
                            }
                            className="text-xs flex items-center gap-1"
                            title={getValidationMessage(type)}
                          >
                            <span>{getValidationIcon(type)}</span>
                            <span>{type}</span>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Connection Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="projectId">Project ID *</Label>
              <Input
                id="projectId"
                placeholder="abc123def"
                value={sanitySettings.projectId}
                onChange={(e) =>
                  setSanitySettings((prev) => ({
                    ...prev,
                    projectId: e.target.value.trim(),
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Found in your Sanity project settings
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dataset">Dataset *</Label>
              <Input
                id="dataset"
                placeholder="production"
                value={sanitySettings.dataset}
                onChange={(e) =>
                  setSanitySettings((prev) => ({
                    ...prev,
                    dataset: e.target.value.trim(),
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Usually "production" or "staging"
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="token">API Token *</Label>
              <Input
                id="token"
                type="password"
                placeholder="sk..."
                value={sanitySettings.token}
                onChange={(e) =>
                  setSanitySettings((prev) => ({
                    ...prev,
                    token: e.target.value.trim(),
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Your Sanity API token with Editor permissions
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="studioUrl">Studio URL (Optional)</Label>
              <Input
                id="studioUrl"
                placeholder="https://creatorli.sanity.studio"
                value={sanitySettings.studioUrl}
                onChange={(e) =>
                  setSanitySettings((prev) => ({
                    ...prev,
                    studioUrl: e.target.value.trim(),
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Your custom Sanity Studio URL (leave empty for default:{" "}
                {sanitySettings.projectId}.sanity.studio)
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={testConnection}
                disabled={
                  testingConnection ||
                  !sanitySettings.projectId ||
                  !sanitySettings.dataset ||
                  !sanitySettings.token
                }
                className="flex-1"
              >
                {testingConnection
                  ? "Testing..."
                  : connectionTested
                    ? "✓ Tested"
                    : "Test Connection"}
              </Button>
              {connectionTested && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fetchDocumentTypes}
                  disabled={fetchingDocTypes}
                >
                  {fetchingDocTypes ? "Fetching..." : "Refresh Types"}
                </Button>
              )}
            </div>

            {connectionTested && documentTypes.length > 0 && (
              <>
                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium text-sm">
                    Publishing Configuration
                  </h4>

                  {/* Auto-publish toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="auto-publish">
                        Auto-publish articles
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically publish articles to Sanity when they're
                        created
                      </p>
                    </div>
                    <Switch
                      id="auto-publish"
                      checked={sanitySettings.autoPublish}
                      onCheckedChange={(checked) =>
                        setSanitySettings((prev) => ({
                          ...prev,
                          autoPublish: checked,
                        }))
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="documentType">Document Type</Label>
                    <Select
                      value={sanitySettings.selectedDocumentType}
                      onValueChange={(value) =>
                        setSanitySettings((prev) => ({
                          ...prev,
                          selectedDocumentType: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select document type" />
                      </SelectTrigger>
                      <SelectContent>
                        {documentTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Choose the document type for published articles
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="defaultAuthor">Default Author</Label>
                    <Input
                      id="defaultAuthor"
                      placeholder="e.g., John Doe or author document ID"
                      value={sanitySettings.defaultAuthor}
                      onChange={(e) =>
                        setSanitySettings((prev) => ({
                          ...prev,
                          defaultAuthor: e.target.value,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Default author to assign to published articles (name or
                      reference ID)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Publishing Options</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="returnIds"
                          checked={
                            sanitySettings.defaultPublishOptions?.returnIds
                          }
                          onCheckedChange={(checked) =>
                            setSanitySettings((prev) => ({
                              ...prev,
                              defaultPublishOptions: {
                                ...prev.defaultPublishOptions!,
                                returnIds: !!checked,
                              },
                            }))
                          }
                        />
                        <Label htmlFor="returnIds" className="text-sm">
                          Return document IDs
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="returnDocuments"
                          checked={
                            sanitySettings.defaultPublishOptions
                              ?.returnDocuments
                          }
                          onCheckedChange={(checked) =>
                            setSanitySettings((prev) => ({
                              ...prev,
                              defaultPublishOptions: {
                                ...prev.defaultPublishOptions!,
                                returnDocuments: !!checked,
                              },
                            }))
                          }
                        />
                        <Label htmlFor="returnDocuments" className="text-sm">
                          Return full documents
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="visibility">Visibility Mode</Label>
                    <Select
                      value={sanitySettings.defaultPublishOptions?.visibility}
                      onValueChange={(value: "sync" | "async" | "deferred") =>
                        setSanitySettings((prev) => ({
                          ...prev,
                          defaultPublishOptions: {
                            ...prev.defaultPublishOptions!,
                            visibility: value,
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sync">Sync (Immediate)</SelectItem>
                        <SelectItem value="async">
                          Async (Background)
                        </SelectItem>
                        <SelectItem value="deferred">
                          Deferred (Batch)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Controls how mutations are processed by Sanity
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="pt-2">
              <div className="text-xs text-muted-foreground">
                <p>After connecting, you'll be able to:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Publish articles directly to Sanity</li>
                  <li>Upload and manage images</li>
                  <li>Maintain SEO metadata</li>
                  <li>Create structured content</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSanityConnect}
            disabled={
              loading ||
              !connectionTested ||
              !sanitySettings.projectId ||
              !sanitySettings.dataset ||
              !sanitySettings.token
            }
          >
            {loading ? "Connecting..." : "Connect & Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
